import React, { useEffect, useRef, useState } from 'react';
import { fy, type Problem, type Rec } from './api';
import { BookmarkIcon, CloseIcon, EditIcon, ShareIcon } from './icons';
import { fyTrack, useImpression } from './track';

interface CardProps {
  rec: Rec;
  rank: number;
  saved: boolean;
  surface: string;
  onOpen: (r: Rec, rank: number, surface: string) => void;
  onBookmark: (r: Rec, on: boolean, surface: string) => void;
  /** With onSelect, a checkbox to pick it for sharing (tracker, 2026-10-29). */
  selected?: boolean;
  onSelect?: (r: Rec) => void;
}

export const RecCard: React.FC<CardProps> = ({ rec, rank, saved, surface, onOpen, onBookmark, selected, onSelect }) => {
  const src = [rec.university, rec.area].filter(Boolean).join(' · ');
  return (
    <article className="fy-card" role="button" tabIndex={0}
             onClick={() => onOpen(rec, rank, surface)}
             onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(rec, rank, surface); } }}>
      <div className="fy-card-top">
        {onSelect && (
          <input type="checkbox" className="fy-check" checked={!!selected} aria-label={`Select ${rec.title} to share`}
                 style={{ marginTop: 6 }} onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}
                 onChange={() => onSelect(rec)} />
        )}
        <div className="fy-src" style={onSelect ? { flex: 1 } : undefined}>{src || 'University technology'}</div>
        <button type="button" className="fy-icon-btn" aria-pressed={saved}
                aria-label={saved ? `Remove ${rec.title} from saved` : `Save ${rec.title}`}
                title={saved ? 'Remove from saved' : 'Save'}
                onClick={e => { e.stopPropagation(); onBookmark(rec, !saved, surface); }}>
          <BookmarkIcon filled={saved} />
        </button>
      </div>
      <h3 className="fy-h3">{rec.title}</h3>
      <span className={`fy-pill ${rec.match === 'strong' ? 'fy-pill-strong' : ''}`}
            title={`${rec.relevance}% relevance`}>
        {rec.match === 'strong' ? 'Strong match' : 'Good match'}
      </span>
    </article>
  );
};

interface Props {
  problem: Problem;
  topShown: number;
  savedIds: Set<string>;
  onOpen: (r: Rec, rank: number, surface: string) => void;
  onBookmark: (r: Rec, on: boolean, surface: string) => void;
  onEdit: () => void;
  /** Tech offers picked to share (ids), and the share action with the picked cards. */
  selected: Set<string>;
  setSelected: (s: Set<string>) => void;
  onShare: (items: Rec[]) => void;
}

const RecommendedSection: React.FC<Props> = ({ problem, topShown, savedIds, onOpen, onBookmark, onEdit,
                                               selected, setSelected, onShare }) => {
  const ref = useImpression<HTMLElement>('recommended');
  const [all, setAll] = useState(false);
  const recs = problem.recs.slice(0, topShown);
  const more = problem.match_count > 0;
  // Every card seen in this section (the grid and the View-all list), so a pick
  // made in either can be shared.
  const known = useRef(new Map<string, Rec>());
  recs.forEach(r => known.current.set(r.id, r));
  const picked = [...selected].map(id => known.current.get(id)).filter((r): r is Rec => !!r);
  const n = picked.length;
  const allOn = recs.length > 0 && recs.every(r => selected.has(r.id));
  const toggle = (r: Rec) => {
    const next = new Set(selected);
    if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
    fyTrack('foryou_rec_select', { tech_id: r.id, on: next.has(r.id), count: next.size });
    setSelected(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    recs.forEach(r => { if (allOn) next.delete(r.id); else next.add(r.id); });
    fyTrack('foryou_rec_select_all', { on: !allOn, count: next.size });
    setSelected(next);
  };

  return (
    <section ref={ref} className="fy-sec" aria-label="Recommended for this problem">
      <div className="fy-sec-head">
        <h2 className="fy-h2">Recommended for this problem</h2>
        {problem.status === 'done' && more && (
          <button type="button" className="fy-link" onClick={() => {
            fyTrack('foryou_view_all', { problem_position: problem.position, count: problem.match_count });
            setAll(true);
          }}>View all →</button>
        )}
      </div>
      {problem.status === 'done' && recs.length > 0 && (
        <>
          <div className="fy-selbar">
            <div className="fy-row">
              <input id="fy-rec-all" type="checkbox" className="fy-check" checked={allOn} onChange={toggleAll} />
              <label htmlFor="fy-rec-all" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Select all</label>
              <span className="fy-muted">· {n === 0 ? 'select tech offers to share' : `${n} selected`}</span>
            </div>
            <button type="button" className="fy-btn fy-btn-primary" onClick={() => onShare(picked)} disabled={n === 0}>
              <ShareIcon /> Share
            </button>
          </div>
          <div className="fy-grid">
            {recs.map((r, i) => (
              <RecCard key={r.id} rec={r} rank={i + 1} saved={savedIds.has(r.id)} surface="foryou_recommended"
                       onOpen={onOpen} onBookmark={onBookmark} selected={selected.has(r.id)} onSelect={toggle} />
            ))}
          </div>
        </>
      )}
      {problem.status === 'pending' && (
        <div role="status" aria-busy="true">
          <p className="fy-muted" style={{ margin: '0 0 10px' }}>
            Finding the best tech offers for this problem…
            {problem.retrying ? ' This is taking longer than usual — we will try again shortly.' : ''}
          </p>
          <div className="fy-grid">{[0, 1, 2, 3].map(i => <div key={i} className="fy-skel" />)}</div>
        </div>
      )}
      {(problem.status === 'empty' || (problem.status === 'done' && recs.length === 0)) && (
        <div className="fy-empty">
          <div className="fy-empty-title">No close matches yet</div>
          <p className="fy-muted">
            Try broadening the statement: name the technology area or the outcome you need rather than one specific
            product, and leave out company names.
          </p>
          <button type="button" className="fy-btn fy-btn-quiet fy-btn-sm" onClick={onEdit}><EditIcon /> Edit</button>
        </div>
      )}
      {all && (
        <ViewAllDialog problem={problem} savedIds={savedIds} onClose={() => setAll(false)}
                       onOpen={onOpen} onBookmark={onBookmark} selected={selected} onSelect={toggle}
                       onLoaded={items => items.forEach(r => known.current.set(r.id, r))}
                       onShare={() => { setAll(false); onShare([...selected].map(id => known.current.get(id)).filter((r): r is Rec => !!r)); }} />
      )}
    </section>
  );
};

const ViewAllDialog: React.FC<{
  problem: Problem; savedIds: Set<string>; onClose: () => void;
  onOpen: (r: Rec, rank: number, surface: string) => void;
  onBookmark: (r: Rec, on: boolean, surface: string) => void;
  selected: Set<string>; onSelect: (r: Rec) => void; onLoaded: (items: Rec[]) => void; onShare: () => void;
}> = ({ problem, savedIds, onClose, onOpen, onBookmark, selected, onSelect, onLoaded, onShare }) => {
  const [items, setItems] = useState<Rec[] | null>(null);
  const [err, setErr] = useState('');
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    let live = true;
    fy.matches(problem.id).then(r => {
      if (!live) return;
      if (r.ok && r.data) { onLoaded(r.data.items); setItems(r.data.items); } else setErr(r.error);
    });
    return () => { live = false; };
  }, [problem.id]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // The technology opens in the app's own modal: this list closes first.
  const open = (r: Rec, rank: number, surface: string) => { onClose(); onOpen(r, rank, surface); };

  return (
    <div className="fyx fy-overlay" onClick={onClose}>
      <div className="fy-dialog fy-dialog-wide" role="dialog" aria-modal="true" aria-label="All matches"
           onClick={e => e.stopPropagation()}>
        <button type="button" className="fy-icon-btn fy-close" aria-label="Close" onClick={onClose}><CloseIcon /></button>
        <div>
          <div className="fy-kicker">All matches · Problem {problem.position}</div>
          <h2 className="fy-h2" style={{ marginTop: 4, paddingRight: 36 }}>“{problem.text}”</h2>
        </div>
        {items && items.length > 0 && (
          <div className="fy-selbar">
            <span className="fy-muted">{selected.size === 0 ? 'Tick tech offers to share them in one message' : `${selected.size} selected`}</span>
            <button type="button" className="fy-btn fy-btn-primary" onClick={onShare} disabled={selected.size === 0}>
              <ShareIcon /> Share
            </button>
          </div>
        )}
        {!items && !err && <div className="fy-grid">{[0, 1, 2, 3].map(i => <div key={i} className="fy-skel" />)}</div>}
        {err && <div className="fy-err">{err}</div>}
        {items && items.length === 0 && <p className="fy-muted">No matches stored for this statement yet.</p>}
        {items && items.length > 0 && (
          <div className="fy-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {items.map((r, i) => (
              <RecCard key={r.id} rec={r} rank={i + 1} saved={savedIds.has(r.id)} surface="foryou_view_all"
                       onOpen={open} onBookmark={onBookmark} selected={selected.has(r.id)} onSelect={onSelect} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendedSection;
